/**
 * Tooltip Utility
 *
 * Shared tooltip functionality for consistent tooltips across the application.
 * Supports configurable positioning, delay, and both static (data-tooltip) and
 * dynamic (callback) text sources.
 *
 * @example
 * // Static tooltip using data-tooltip attribute
 * Tooltip.setup(buttonElement, { position: 'below' });
 *
 * // Dynamic tooltip with callback
 * Tooltip.setup(buttonElement, {
 *   position: 'above',
 *   getText: () => isSubmitting ? 'Stop' : 'Submit'
 * });
 *
 * // Setup multiple elements with data-tooltip attributes
 * Tooltip.setupAll(container, '[data-tooltip]', { position: 'right' });
 */

/** @constant {number} Default delay before showing tooltip (ms) */
const DEFAULT_DELAY_MS = 0;

/** @constant {number} Gap between element and tooltip (px) */
const TOOLTIP_GAP = 5;

/**
 * @typedef {'above' | 'below' | 'below-end' | 'left' | 'right'} TooltipPosition
 * `below-end` sits under the element with right edges aligned — for controls
 * near the viewport's right edge, where a centred/left tooltip would overflow
 * or cover an adjacent control.
 */

/**
 * @typedef {Object} TooltipOptions
 * @property {TooltipPosition} [position='below'] - Where to position the tooltip
 *   (a `data-tooltip-position` attribute on the element wins over this)
 * @property {Function} [getText] - Callback to get tooltip text (overrides data-tooltip)
 * @property {number} [delay=100] - Delay before showing tooltip (ms)
 * @property {Function} [shouldShow] - Callback to determine if tooltip should show
 * @property {boolean} [html=false] - If true, render tooltip content as HTML instead of text
 * @property {string} [className] - Extra class(es) added to the tooltip element (variant styling)
 */

export const Tooltip = {
  /**
   * Set up tooltip behavior for a single element
   * @param {HTMLElement} element - Element to attach tooltip to
   * @param {TooltipOptions} [options={}] - Tooltip configuration
   * @returns {Function} Cleanup function to remove tooltip listeners
   */
  setup(element, options = {}) {
    if (!element) return () => {};

    const {
      position: defaultPosition = 'below',
      getText = null,
      delay = DEFAULT_DELAY_MS,
      shouldShow = () => true,
      html = false,
      className = null
    } = options;

    // Per-element override so a single setupAll() sweep can still place one
    // button's tooltip differently (e.g. a right-edge button that would run
    // off-screen below). Re-read on every hover rather than captured at setup,
    // so a control whose surroundings change can move its tooltip with them —
    // the sidebar toggle flips right→below when the sidebar expands, because
    // "below" would cover the first nav item while collapsed.
    const resolvePosition = () =>
      element.getAttribute('data-tooltip-position') || defaultPosition;

    let tooltipInstance = null;
    let showTimeout = null;

    const handleMouseEnter = () => {
      // Check if tooltip should show
      if (!shouldShow()) return;

      // Get tooltip text
      const text = getText ? getText() : element.getAttribute('data-tooltip');
      if (!text) return;

      // Delay tooltip appearance
      showTimeout = setTimeout(() => {
        tooltipInstance = Tooltip.create(element, text, resolvePosition(), html, className);
      }, delay);
    };

    const handleMouseLeave = () => {
      // Clear timeout if mouse leaves before tooltip appears
      if (showTimeout) {
        clearTimeout(showTimeout);
        showTimeout = null;
      }

      // Remove tooltip if it exists
      if (tooltipInstance) {
        Tooltip.remove(tooltipInstance);
        tooltipInstance = null;
      }
    };

    // Hide on click: the click usually changes the state the tooltip text
    // describes (and can resize the element under the cursor), so a lingering
    // tooltip is both stale and in the way. mouseenter won't refire while the
    // pointer stays inside, so it stays hidden until a genuine re-hover —
    // which re-reads getText() and shows the fresh text.
    const handleClick = handleMouseLeave;

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('click', handleClick);

    // Return cleanup function
    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mouseleave', handleMouseLeave);
      element.removeEventListener('click', handleClick);
      if (showTimeout) clearTimeout(showTimeout);
      if (tooltipInstance) Tooltip.remove(tooltipInstance);
    };
  },

  /**
   * Set up tooltips for multiple elements within a container
   * @param {HTMLElement} container - Container element to search within
   * @param {string} selector - CSS selector for tooltip elements
   * @param {TooltipOptions} [options={}] - Tooltip configuration for all elements
   * @returns {Function} Cleanup function to remove all tooltip listeners
   */
  setupAll(container, selector, options = {}) {
    if (!container) return () => {};

    const elements = container.querySelectorAll(selector);
    const cleanupFunctions = [];

    elements.forEach(element => {
      const cleanup = Tooltip.setup(element, options);
      cleanupFunctions.push(cleanup);
    });

    // Return cleanup function that removes all tooltips
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  },

  /**
   * Create and position a tooltip element
   * @param {HTMLElement} element - Element to show tooltip for
   * @param {string} text - Tooltip text or HTML content
   * @param {TooltipPosition} position - Where to position the tooltip
   * @param {boolean} [useHtml=false] - If true, set content as innerHTML
   * @param {string|null} [className=null] - Extra class(es) to add (e.g. a variant modifier)
   * @returns {HTMLElement} The created tooltip element
   */
  create(element, text, position = 'below', useHtml = false, className = null) {
    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.className = className ? `tooltip ${className}` : 'tooltip';
    if (useHtml) {
      tooltip.innerHTML = text;
    } else {
      tooltip.textContent = text;
    }
    document.body.appendChild(tooltip);

    // Position tooltip
    Tooltip.position(tooltip, element, position);

    // Trigger fade-in
    requestAnimationFrame(() => {
      tooltip.classList.add('tooltip--visible');
    });

    return tooltip;
  },

  /**
   * Position a tooltip relative to an element
   * @param {HTMLElement} tooltip - Tooltip element
   * @param {HTMLElement} element - Reference element
   * @param {TooltipPosition} position - Where to position the tooltip
   */
  position(tooltip, element, position) {
    const rect = element.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    let left, top;

    switch (position) {
      case 'above':
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
        top = rect.top - tooltipRect.height - TOOLTIP_GAP;
        break;
      case 'below':
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
        top = rect.bottom + TOOLTIP_GAP;
        break;
      case 'below-end':
        left = rect.right - tooltipRect.width;
        top = rect.bottom + TOOLTIP_GAP;
        break;
      case 'left':
        left = rect.left - tooltipRect.width - TOOLTIP_GAP;
        top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
        break;
      case 'right':
        left = rect.right + TOOLTIP_GAP;
        top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
        break;
      default:
        // Default to below
        left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
        top = rect.bottom + TOOLTIP_GAP;
    }

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  },

  /**
   * Remove a tooltip from the DOM with fade-out
   * @param {HTMLElement} tooltip - Tooltip element to remove
   */
  remove(tooltip) {
    if (!tooltip) return;

    // Fade out
    tooltip.classList.remove('tooltip--visible');

    // Remove from DOM after transition
    setTimeout(() => {
      if (tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    }, 50);
  },

  /**
   * Immediately remove all tooltips from the DOM
   * Useful when navigating away or closing modals
   */
  removeAll() {
    const tooltips = document.querySelectorAll('.tooltip');
    tooltips.forEach(tooltip => {
      if (tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    });
  }
};
